<!-- src/views/pages/superadmin/subscription-table.vue -->
<script setup>
import { computed, getCurrentInstance, onMounted } from "vue";
import { useSubscriptionStore } from "@/stores/subscriptionStore";

const emit = defineEmits(["edit"]);
const store = useSubscriptionStore();
const { proxy } = getCurrentInstance();

const columns = [
  { sorter: false },
  {
    title: "Subscriber",
    dataIndex: "subscriber",
    key: "subscriber",
    sorter: true,
  },
  {
    title: "Plan",
    dataIndex: "plan_label",
    sorter: true,
  },
  {
    title: "Plan Price",
    dataIndex: "plan_price",
    sorter: true,
  },
  {
    title: "Annual Licence Fee",
    dataIndex: "license_fee",
    sorter: true,
  },
  {
    title: "Payment Method",
    dataIndex: "payment_method",
    sorter: true,
  },
  {
    title: "Amount",
    dataIndex: "amount_display",
    sorter: true,
  },
  {
    title: "Created Date",
    dataIndex: "created_date",
    sorter: true,
  },
  {
    title: "Expiring On",
    dataIndex: "expiring_on",
    sorter: true,
  },
  {
    title: "Access Status",
    dataIndex: "status",
    key: "status",
    sorter: true,
  },
  {
    title: "Status",
    dataIndex: "payment_status",
    key: "payment_status",
    sorter: true,
  },
  {
    title: "",
    key: "action",
    sorter: false,
  },
];

const rowSelection = {
  onChange: () => {},
  onSelect: () => {},
  onSelectAll: () => {},
};

const dataSource = computed(() => {
  const raw = store.items;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.data)) return raw.data; // in case API wraps
  return [];
});

onMounted(() => {
   if (!dataSource.value.length) {
    store.fetchSubscriptions();
  }
});

function badgeClass(status) {
  if (status === "active") return "badge-success";
  if (status === "grace_period") return "badge-warning";
  if (status === "locked" || status === "expired" || status === "suspended") {
    return "badge-danger";
  }
  return "badge-secondary";
}

function canReactivate(record) {
  return ["locked", "expired", "suspended", "grace_period"].includes(record.status);
}

function canSuspend(record) {
  return ["trialing", "active", "grace_period"].includes(record.status);
}

function canLock(record) {
  return ["trialing", "active", "grace_period"].includes(record.status);
}

async function reactivate(record) {
  const ok = await confirmAction({
    title: "Reactivate subscription?",
    text: `This will renew or reactivate ${record.subscriber}'s subscription.`,
    confirmButtonText: "Yes, reactivate",
  });
  if (!ok) return;
  await store.updateSubscription(record.id, { reactivate: true });
}

async function suspend(record) {
  const ok = await confirmAction({
    title: "Suspend subscription?",
    text: `This will suspend ${record.subscriber}'s subscription access.`,
    confirmButtonText: "Yes, suspend",
  });
  if (!ok) return;
  await store.updateSubscription(record.id, { status: "suspended" });
}

async function lock(record) {
  const ok = await confirmAction({
    title: "Lock subscription?",
    text: `This will lock ${record.subscriber}'s subscription immediately.`,
    confirmButtonText: "Yes, lock",
  });
  if (!ok) return;
  await store.updateSubscription(record.id, { status: "locked" });
}

async function remove(record) {
  const ok = await confirmAction({
    title: "Delete subscription?",
    text: `This will permanently delete the subscription record for ${record.subscriber}.`,
    confirmButtonText: "Yes, delete",
    confirmButtonClass: "btn btn-danger",
  });
  if (!ok) return;
  await store.removeSubscription(record.id);
}

async function confirmAction({
  title,
  text,
  confirmButtonText = "Confirm",
  confirmButtonClass = "btn btn-primary",
}) {
  const result = await proxy?.$swal?.({
    title,
    text,
    type: "warning",
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText: "Cancel",
    confirmButtonClass,
    cancelButtonClass: "btn btn-light me-2",
    buttonsStyling: false,
    reverseButtons: true,
  });

  return !!result?.value;
}
</script>

<template>
  <a-table
    class="table datatable thead-light"
    :columns="columns"
    :data-source="dataSource"
    :row-selection="rowSelection"
    :loading="store.loading"
    row-key="id"
  >
    <template #bodyCell="{ column, record }">
      <!-- Subscriber with logo -->
      <template v-if="column.key === 'subscriber'">
        <div class="d-flex align-items-center file-name-icon">
          <span class="avatar avatar-md border rounded-circle">
            <img
              v-if="record.company_logo_url"
              :src="record.company_logo_url"
              class="img-fluid"
              alt="img"
            />
            <span
              v-else
              class="d-flex align-items-center justify-content-center w-100 h-100"
            >
              {{ record.subscriber?.charAt(0) ?? "C" }}
            </span>
          </span>
          <div class="ms-2">
            <h6 class="fw-medium mb-0">
              {{ record.subscriber }}
            </h6>
          </div>
        </div>
      </template>

      <!-- Payment status badge -->
      <template v-else-if="column.key === 'payment_status'">
        <span
          class="d-flex align-items-center badge-xs"
          :class="[
            'badge',
            record.payment_status === 'paid'
              ? 'badge-success'
              : record.payment_status === 'unpaid'
              ? 'badge-danger'
              : 'badge-warning',
          ]"
        >
          <i class="ti ti-point-filled me-1"></i>
          {{
            record.payment_status.charAt(0).toUpperCase() +
            record.payment_status.slice(1)
          }}
        </span>
      </template>

      <template v-else-if="column.key === 'status'">
        <span
          class="d-flex align-items-center badge-xs"
          :class="['badge', badgeClass(record.status)]"
        >
          <i class="ti ti-point-filled me-1"></i>
          {{ String(record.status || "-").replaceAll("_", " ") }}
        </span>
      </template>

      <template v-else-if="column.dataIndex === 'plan_price'">
        {{ record.currency }} {{ Number(record.plan_price || 0).toFixed(2) }}
      </template>

      <template v-else-if="column.dataIndex === 'license_fee'">
        {{ record.currency }} {{ Number(record.license_fee || 0).toFixed(2) }}
      </template>

      <!-- Actions -->
      <template v-else-if="column.key === 'action'">
        <div class="action-icon d-inline-flex">
          <a
            href="javascript:void(0);"
            title="Edit subscription"
            @click="emit('edit', record)"
          >
            <i class="ti ti-edit"></i>
          </a>
          <a
            v-if="canReactivate(record)"
            href="javascript:void(0);"
            title="Reactivate / renew"
            @click="reactivate(record)"
          >
            <i class="ti ti-refresh"></i>
          </a>
          <a
            v-if="canLock(record)"
            href="javascript:void(0);"
            title="Lock subscription"
            @click="lock(record)"
          >
            <i class="ti ti-lock"></i>
          </a>
          <a
            v-if="canSuspend(record)"
            href="javascript:void(0);"
            title="Suspend subscription"
            @click="suspend(record)"
          >
            <i class="ti ti-player-pause"></i>
          </a>
          <a
            href="javascript:void(0);"
            title="Delete subscription"
            @click="remove(record)"
          >
            <i class="ti ti-trash"></i>
          </a>
        </div>
      </template>
    </template>
  </a-table>
</template>
